import {
  Box,
  Button,
  CircularProgress,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ErrorTypography from '../../../shared/ErrorTypography/ErrorTypography';
import { createIconFromName } from '../../../util/iconUtil';
import { useGetServicesQuery, useGetServiceDocumentationQuery } from '../../../store/service/serviceSlice';
import { Service } from '../../../store/service/types';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { nameTranslator } from '../../../locales/NameTranslator';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

type ServiceDisplayType = {
  icon: JSX.Element;
  name: string;
  module: string;
  service: string;
  parameters: string;
  results: string;
};

const AlgorithmsTable = (): JSX.Element => {
  const { data, error, isLoading } = useGetServicesQuery(undefined);
  const { t } = useTranslation();

  const prepareService = (service: Service): ServiceDisplayType => {
    const parameters = `${service.parameters.map(param => t(param.name_de)).join(', ')} (${service.parameters.length})`;
    const results = `${service.results.map(param => t(param.name)).join(', ')} (${service.results.length})`;
    return {
      icon: createIconFromName(service.icon),
      name: nameTranslator(service),
      module: nameTranslator(data?.modules.filter(module => module.module === service.module)[0]),
      service: service.service,
      parameters,
      results,
    };
  };

  if (error) {
    // TODO: use internationalization
    return <ErrorTypography message={'Error getting pipelines'} />;
  } else {
    return (
      <TableContainer>
        <Table aria-label={'algorithms table'}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t('Name')}</TableCell>
              <TableCell>{t('Module')}</TableCell>
              <TableCell>{t('Parameters')}</TableCell>
              <TableCell>{t('ReturnValues')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading && data ? (
              data.services.map(prepareService).map(service => (
                <StyledTableRow key={service.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component={'th'} scope={'row'}>
                    {service.icon}
                  </TableCell>
                  <TableCell component={'th'} scope={'row'}>
                    <Button component={Link} color={'inherit'} to={`/algorithm/${service.service}`} style={{textTransform: 'none', color:'blue'}}>
                      <Trans>{service.name}</Trans>
                    </Button>
                  </TableCell>
                  <TableCell component={'th'} scope={'row'}>
                    {service.module}
                  </TableCell>
                  <TableCell component={'th'} scope={'row'}>
                    {service.parameters}
                  </TableCell>
                  <TableCell component={'th'} scope={'row'}>
                    {service.results}
                  </TableCell>
                </StyledTableRow>
              ))
            ) : (
              <Box sx={{ p: 2 }}>
                <CircularProgress />
              </Box>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
};

export default AlgorithmsTable;
